import { useEffect, useMemo, useState } from "react"

import { Checkbox, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Button from "../../../../../Components/Button"
import Modal from "../../../../../Components/Modal"
import Select from "../../../../../Components/Select"
import Icon from "../../../../../Icon"
import {
  assignmentRuleConflict,
  conditionsConflict,
  ruleConditionsSummary
} from "../utils/ruleSummary"
import ConditionsBuilder from "./ConditionsBuilder/ConditionsBuilder"

import styles from "./ruleModal.module.less"

const { Text } = Typography

// Add / edit an assignment rule: route disputes matching the conditions
// (category / amount / payment processor / card network / issuer) to a specific team
// member. Local form state only; commits via onSave (parent handles confirm + toast).
const AssignmentRuleModal = ({
  visible,
  onClose,
  onSave,
  initial,
  categoryOptions,
  members = [],
  otherRules = []
}) => {
  const { t } = useTranslation()
  // The shared Modal switches to its bottom-sheet layout via this PROP (not redux):
  // without it, centered rendering collides with the mobile sheet CSS and the whole
  // popup lands off-screen at phone widths.
  const isMobile = useSelector((state) => state.nav.isMobile)
  const [assignee, setAssignee] = useState(undefined)
  // Which case types the rule covers. Required: a rule that applies to nothing routes
  // nothing. Defaults to both, and rules saved before this field existed mean both.
  const [types, setTypes] = useState(["dispute", "inquiry"])
  const [conditions, setConditions] = useState([])

  useEffect(() => {
    if (!visible) return
    setAssignee(initial?.assignee ?? undefined)
    setTypes(
      Array.isArray(initial?.types) && initial.types.length
        ? [...initial.types]
        : ["dispute", "inquiry"]
    )
    setConditions(
      Array.isArray(initial?.conditions) && initial.conditions.length
        ? initial.conditions.map((c) => ({ ...c }))
        : [{ field: "amount", op: "gte", value: "" }]
    )
  }, [visible, initial])

  const memberOptions = useMemo(
    () => members.map((m) => ({ value: m.id, label: m.name })),
    [members]
  )
  const nameOf = (id) => (members.find((m) => m.id === id) || {}).name || t("Unassigned")
  const assigneeName = (members.find((m) => m.id === assignee) || {}).name

  // Same two-layer validation the exception modal uses. Within the rule: conditions are
  // AND-ed, so contradictions (two lower amount bounds, is + is not on one value) can
  // never match. Across rules: two rules matching the same dispute have no defined
  // winner, so an overlap is a real ambiguity here, not just redundancy.
  const filledConditions = conditions.filter((c) => c.value !== "" && c.value != null)
  const conflict =
    conditionsConflict(conditions, t) || assignmentRuleConflict(conditions, otherRules, nameOf, t)
  const canSave = assignee != null && types.length > 0 && filledConditions.length > 0 && !conflict
  const handleSave = () => {
    if (!canSave) return
    onSave({ id: initial?.id, assignee, types, conditions: filledConditions })
  }
  const typesLabel =
    types.length === 2
      ? t("Disputes and inquiries")
      : types.includes("inquiry")
        ? t("Inquiries only")
        : types.length
          ? t("Disputes only")
          : t("None")

  // Same modal shell as the Alerts Auto-refund setup (and the Add rule modal):
  // clear-bg content, description under the title, Cancel/Save inside the body.
  return (
    <Modal
      visible={visible}
      isMobile={isMobile}
      handleClose={onClose}
      title={initial ? t("Edit assignment rule") : t("Add assignment rule")}
      footer={null}
      destroyOnClose
      width={680}
      closeIcon={<Icon type="cross_small" />}
      classNames={{ content: "clear-bg" }}
      className="change-refund-form-modal"
    >
      <Text type="secondary">
        {t(
          "Cases that match every condition below are assigned to the team member you pick, and that person is emailed. The case stays visible to everyone either way."
        )}
      </Text>
      <div className={styles.root} style={{ marginTop: 16 }}>
        <div className={styles.field}>
          <Text strong>{t("Assign to")}</Text>
          <Select
            size="large"
            value={assignee}
            placeholder={t("Select a team member")}
            options={memberOptions}
            onChange={setAssignee}
            notFoundContent={t("No team members yet")}
            style={{ width: "100%" }}
          />
        </div>

        <div className={styles.field}>
          <Text strong>{t("Type")}</Text>
          <Text type="secondary" className={styles.hint}>
            {t("Which case types this rule routes. Pick at least one.")}
          </Text>
          <Checkbox.Group
            value={types}
            onChange={(v) => setTypes(v)}
            options={[
              { label: t("Disputes"), value: "dispute" },
              { label: t("Inquiries"), value: "inquiry" }
            ]}
          />
        </div>

        <div className={styles.field}>
          <Text strong>{t("Conditions")}</Text>
          <Text type="secondary" className={styles.hint}>
            {t(
              "Match on category, amount, payment processor, card network, or issuer. All conditions must match."
            )}
          </Text>
          <ConditionsBuilder
            conditions={conditions}
            onChange={setConditions}
            categoryOptions={categoryOptions}
            includeProcessor
          />
        </div>

        <div className={styles.summary}>
          <Text strong className={styles.summaryTitle}>
            {t("Summary")}
          </Text>
          <Text>
            <Text strong>{t("Assign to")}:</Text> {assigneeName || t("None")}
          </Text>
          <Text>
            <Text strong>{t("Type")}:</Text> {typesLabel}
          </Text>
          <Text>
            <Text strong>{t("When")}:</Text> {ruleConditionsSummary(conditions, t)}
          </Text>
        </div>
      </div>

      {conflict ? (
        <Flex align="center" gap={8} style={{ marginTop: 16 }}>
          <Icon type="alert_triangle_error" style={{ fontSize: 20 }} />
          <Text type="danger">{conflict}</Text>
        </Flex>
      ) : null}

      <Flex gap={16} justify="end" style={{ padding: "0 3px 3px 0", marginTop: 24 }}>
        <Button size="large" htmlType="button" onClick={onClose} text={t("Cancel")} />
        <Button
          size="large"
          type="primary"
          disabled={!canSave}
          onClick={handleSave}
          text={t("Save rule")}
        />
      </Flex>
    </Modal>
  )
}

export default AssignmentRuleModal
